<template>
  <component
    :is="wrapperElement"
    class="auto-form"
    :class="[ isTemplateScoped && 'auto-form--scoped' ]"
    :data-test="`${formId}-wrapper`"
    @submit.prevent
  >
    <div
      v-for="(fields, index) of fieldsInColumns"
      :key="index"
      class="auto-form-row"
    >
      <template v-for="field of fields">
        <DynamicField
          v-if="field.type === 'question' || field.type === 'custom'"
          :key="field.id"
          :show="isElementInScope(field.id)"
          class="auto-form-column"
          :questionId="field.id"
          :formId="formId"
          :formType="formType"
          :dataScope="templateScopeTail"
          :editable="editable"
          :config="config"
          :allowFilePrivacyToggle="allowFilePrivacyToggle"
          :isOnlyFieldInContainer="fieldsInColumns.length === 1 && fields.length === 1"
        />
        <FormCard
          v-if="field.type === 'card'"
          v-show="isVisible(field.config) && isElementInScope(field.id)"
          :key="field.id"
          :hiddenHeader="!!templateScopeTail.length"
          v-bind="field.config"
          class="form-card"
          :node="field"
        >
          <DynamicForm
            :template="field.children"
            :formId="formId"
            :formType="formType"
            :editable="editable"
            :config="getPredefinedCardConfig(field)"
            :templateScope="templateScopeTail"
            :isNested="true"
            :allowFilePrivacyToggle="allowFilePrivacyToggle"
          />
        </FormCard>
        <FormHeadline
          v-if="field.type === 'headline' && isVisible(field.config)"
          :key="field.id"
          v-bind="field.config"
          :node="field"
        />
      </template>
    </div>
  </component>
</template>

<script>
import { computed, onMounted, inject, toRefs } from '@vue/composition-api'
import DynamicField from './DynamicField'
import FormCard from '@/components/Forms/FormCard'
import FormHeadline from '@/components/Forms/FormHeadline'
import store from '@/store'
import { userHasAnyAllowedRole } from '@shared/permissions/validators'

function fieldsToArray (fields) {
  return Object.keys(fields).map(fieldKey => ({
    ...fields[fieldKey],
    dataKey: fieldKey,
    name: fieldKey,
  }))
}

function unwrapRows (field) {
  if (Array.isArray(field)) return field
  else {
    if (field.type === 'row') {
      return field.children
    }
    return field
  }
}

function defaultToQuestionType (field) {
  if (!field) throw Error('Field definition does not exist')
  return {
    ...(typeof field === 'object' ? field : {}),
    id: field.id || field,
    type: field.type || 'question',
  }
}

export default {
  name: 'DynamicForm',
  components: { DynamicField, FormCard, FormHeadline },
  props: {
    template: {
      type: [ Object, Array ],
      default: null,
    },
    columns: {
      type: Number,
      default: 1,
    },
    formId: {
      type: String,
      required: true,
    },
    formType: {
      type: String,
      required: false,
    },
    editable: {
      type: Boolean,
      default: true,
    },
    config: {
      type: Object,
      default: () => ({}),
    },
    isNested: {
      type: Boolean,
      default: false,
    },
    onMountedAction: {
      type: Function,
      default: () => {},
    },
    // It allows to limit view to specific card or field (not working with rows since it is not an element)
    // E.g. `degreeMaps` to display only `degreeMaps` card.
    // `Aep7D.status` to display only status question on `Aep7D` card.
    templateScope: {
      type: [ String, Array ],
      required: false,
    },
    allowFilePrivacyToggle: {
      type: Boolean,
      default: false,
    },
  },
  setup (props) {
    const user = store.state.user
    const { template, isNested, templateScope } = toRefs(props)
    const templateRef = template.value ? template : inject(`${props.formId}_template`)

    onMounted(props.onMountedAction)

    const wrapperElement = computed(() => isNested.value
      ? 'div'
      : 'form'
    )

    const normalizedFields = Array.isArray(templateRef.value)
      ? templateRef.value.map(unwrapRows)
      : fieldsToArray(templateRef.value).map(unwrapRows)

    const normalizedTemplateScope = computed(() => Array.isArray(templateScope.value || [])
      ? (templateScope.value || [])
      : templateScope.value.split('.'))
    const templateScopeHead = computed(() => normalizedTemplateScope.value[0])
    const isTemplateScoped = computed(() => !!templateScopeHead.value)
    const templateScopeTail = computed(() => {
      if (!isTemplateScoped.value) return []
      const [ , ...tail ] = normalizedTemplateScope.value
      return tail
    })

    function isElementInScope (id) {
      if (!isTemplateScoped.value) return true
      return templateScopeHead.value === id
    }

    function getFieldsInColumns (fields) {
      if (props.columns === 'auto' || props.columns === 1) {
        return fields.map(
          fields => Array.isArray(fields) ? fields.map(defaultToQuestionType) : [ defaultToQuestionType(fields) ]
        )
      } else {
        const rowsLength = Math.ceil(fields.length / props.columns)
        return new Array(rowsLength)
          .fill()
          .map((el, index) => fields.slice(
            index * props.columns,
            index * props.columns + props.columns
          ).map(defaultToQuestionType))
      }
    }
    const fieldsInColumns = getFieldsInColumns(normalizedFields)

    function isVisible (config) {
      return userHasAnyAllowedRole(user, null, config.rolesAllowedToSee) && !config.hidden && !config.hiddenCard
    }

    function getPredefinedCardConfig (field) {
      if (field.config?.predefinedContent?.config) {
        return { ...props.config, ...field.config?.predefinedContent?.config, ...field.config }
      } else {
        return { ...props.config, ...field.config }
      }
    }

    return {
      fieldsInColumns,
      isVisible,
      getPredefinedCardConfig,
      wrapperElement,
      isTemplateScoped,
      templateScopeTail,
      isElementInScope,
    }
  },
}
</script>

<style scoped>
.auto-form-column {
  flex-grow: 12;
  width: 100%;
  flex-basis: 150px;
  padding: 0 0.5rem;
}

.auto-form-row {
  display: flex;
  flex-flow: row wrap;
  margin: 0 -0.5rem;
}

.form-card {
  margin-bottom: 0.5rem;
}

.auto-form {
  width: 100%;
  margin-bottom: 0px;
}

.auto-form >>> label {
  display: flex;
  align-items: center;
}
</style>
